import { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'

interface OrgData {
  id: string
  nama: string
  alamat: string | null
  no_telp: string | null
  kota: string | null
  logo_url: string | null
  email: string | null
  primary_color: string | null
  is_rental: boolean
  is_jasa: boolean
  is_tour: boolean
}

interface OrgContextValue {
  orgId: string | null
  org: OrgData | null
  isRental: boolean
  isJasa: boolean
  isTour: boolean
  loading: boolean
  devSetOrg: (orgId: string) => void
}

const OrgContext = createContext<OrgContextValue>({
  orgId: null,
  org: null,
  isRental: false,
  isJasa: false,
  isTour: false,
  loading: true,
  devSetOrg: () => {},
})

export function useOrg() {
  return useContext(OrgContext)
}

const DEV_ORG_KEY = 'dev_org_id'
const IS_DEV = import.meta.env.DEV

function getSubdomain(): string | null {
  const hostname = window.location.hostname
  const parts = hostname.split('.')
  if (parts.length < 3) return null
  return parts[0]
}

const SELECT = 'id, nama, alamat, no_telp, kota, logo_url, email, primary_color, is_rental, is_jasa, is_tour'

export function OrgProvider({ children }: { children: React.ReactNode }) {
  const [orgId, setOrgId] = useState<string | null>(null)
  const [org, setOrg] = useState<OrgData | null>(null)
  const [loading, setLoading] = useState(true)

  async function loadOrg(id: string) {
    const { data } = await supabase
      .from('organization')
      .select(SELECT)
      .eq('id', id)
      .single()
    if (data) { setOrgId(data.id); setOrg(data) }
    setLoading(false)
  }

  useEffect(() => {
    // Dev mode: localStorage override via DevOrgSwitcher
    if (IS_DEV) {
      const devId = localStorage.getItem(DEV_ORG_KEY)
      if (devId) { loadOrg(devId); return }
    }

    // Prod: subdomain detection
    const subdomain = getSubdomain()
    if (subdomain) {
      supabase
        .from('organization')
        .select(SELECT)
        .eq('subdomain', subdomain)
        .single()
        .then(({ data }) => {
          if (data) { setOrgId(data.id); setOrg(data) }
          setLoading(false)
        })
      return
    }

    setLoading(false)
  }, [])

  function devSetOrg(id: string) {
    if (!IS_DEV) return
    localStorage.setItem(DEV_ORG_KEY, id)
    setLoading(true)
    loadOrg(id)
  }

  const isRental = org?.is_rental ?? false
  const isJasa   = org?.is_jasa   ?? false
  const isTour   = org?.is_tour   ?? false

  return (
    <OrgContext.Provider value={{ orgId, org, isRental, isJasa, isTour, loading, devSetOrg }}>
      {children}
    </OrgContext.Provider>
  )
}
